import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, CheckCircle2, ClipboardCheck, Plus, ShieldCheck, Trash2, X, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  Organization,
  PaginatedResponse,
  SupervisionOptions,
  SupervisorResponsibilityTransferResponse,
  SupervisorStoreResponsibility,
  SupervisorVisit,
  SupervisorVisitReport,
  SupervisorVisitReportDecision,
} from '../../types/domain';

interface TransferRow {
  location: string;
  supervisor: string;
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const RESPONSIBILITY_BACKDATE_GRACE_MS = 5 * 60_000;

function localDateTimeValue(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatDuration(seconds: number | null) {
  if (seconds === null) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function SupervisionPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const canManage = isPlatformAdmin || capabilities.includes('supervision:manage');
  const queryClient = useQueryClient();
  const organizations = useApiCollection<Organization>(
    'supervision-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
    isPlatformAdmin,
  );
  const availableOrganizations = useMemo(
    () => (organizations.data?.results ?? []).filter(
      (row) => row.is_active && (row.type === 'agency' || row.type === 'client'),
    ),
    [organizations.data?.results],
  );
  const defaultWorkspace = selectedWorkspace?.organizationId ?? '';
  const [platformWorkspace, setPlatformWorkspace] = useState('');
  const workspaceId = isPlatformAdmin ? platformWorkspace : defaultWorkspace;
  const [effectiveAt, setEffectiveAt] = useState(() => localDateTimeValue());
  const [minimumEffectiveAt] = useState(() => localDateTimeValue(
    new Date(Date.now() - RESPONSIBILITY_BACKDATE_GRACE_MS),
  ));
  const [reason, setReason] = useState('');
  const [renderedAt] = useState(() => Date.now());
  const [transfers, setTransfers] = useState<TransferRow[]>([
    { location: '', supervisor: '' },
  ]);
  const [selectedReport, setSelectedReport] = useState<SupervisorVisitReport | null>(null);
  const [reportDecision, setReportDecision] = useState<'approved' | 'rejected' | null>(null);
  const [reportReviewNote, setReportReviewNote] = useState('');
  const [reportReviewIdempotencyKey, setReportReviewIdempotencyKey] = useState('');

  const options = useQuery({
    queryKey: ['supervision-options', workspaceId, accessToken],
    enabled: Boolean(accessToken && workspaceId && canManage),
    queryFn: () => apiRequest<SupervisionOptions>(
      `/supervisor-responsibilities/options/?workspace=${workspaceId}`,
      accessToken ? { accessToken } : {},
    ),
  });
  const responsibilities = useQuery({
    queryKey: ['supervisor-responsibilities', workspaceId, accessToken],
    enabled: Boolean(accessToken && workspaceId),
    queryFn: () => apiRequest<PaginatedResponse<SupervisorStoreResponsibility>>(
      `/supervisor-responsibilities/?workspace=${workspaceId}&page_size=100&ordering=campaign_location__venue__name,-effective_from`,
      accessToken ? { accessToken } : {},
    ),
  });
  const visits = useQuery({
    queryKey: ['supervisor-visits', workspaceId, accessToken],
    enabled: Boolean(accessToken && workspaceId),
    queryFn: () => apiRequest<PaginatedResponse<SupervisorVisit>>(
      `/supervisor-visits/?workspace=${workspaceId}&page_size=100&ordering=-created_at`,
      accessToken ? { accessToken } : {},
    ),
  });
  const reports = useQuery({
    queryKey: ['supervisor-visit-reports', workspaceId, accessToken],
    enabled: Boolean(accessToken && workspaceId),
    queryFn: () => apiRequest<PaginatedResponse<SupervisorVisitReport>>(
      `/supervisor-visit-reports/?workspace=${workspaceId}&page_size=100&ordering=-created_at`,
      accessToken ? { accessToken } : {},
    ),
  });
  const mutation = useMutation({
    mutationFn: () => apiRequest<SupervisorResponsibilityTransferResponse>(
      '/supervisor-responsibilities/transfer/',
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          effective_at: new Date(effectiveAt).toISOString(),
          reason,
          transfers,
        }),
      },
    ),
    onSuccess: async () => {
      setReason('');
      setTransfers([{ location: '', supervisor: '' }]);
      setEffectiveAt(localDateTimeValue());
      await queryClient.invalidateQueries({ queryKey: ['supervisor-responsibilities'] });
    },
  });
  const reportReviewMutation = useMutation({
    mutationFn: () => apiRequest<SupervisorVisitReportDecision>(
      `/supervisor-visit-reports/${selectedReport?.id}/review/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          decision: reportDecision,
          note: reportReviewNote,
          workspace: workspaceId,
          idempotency_key: reportReviewIdempotencyKey,
        }),
      },
    ),
    onSuccess: async () => {
      setSelectedReport(null);
      setReportDecision(null);
      setReportReviewNote('');
      setReportReviewIdempotencyKey('');
      await queryClient.invalidateQueries({ queryKey: ['supervisor-visit-reports'] });
    },
  });

  const closeReport = () => {
    setSelectedReport(null);
    setReportDecision(null);
    setReportReviewNote('');
    setReportReviewIdempotencyKey('');
  };
  const selectReportDecision = (decision: 'approved' | 'rejected') => {
    setReportDecision(decision);
    setReportReviewIdempotencyKey(crypto.randomUUID());
  };

  const updateTransfer = (index: number, changes: Partial<TransferRow>) => {
    setTransfers((rows) => rows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...changes } : row
    )));
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  const rows = responsibilities.data?.results ?? [];
  const visitRows = visits.data?.results ?? [];
  const reportRows = reports.data?.results ?? [];
  const hasSupervisors = (options.data?.supervisors.length ?? 0) > 0;

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{canManage ? 'Manager workspace' : 'Read-only workspace'}</p>
          <h1>Supervision</h1>
          <p>{canManage ? 'Allocate campaign stores to field supervisors and schedule audited handovers.' : 'View supervisor coverage, store visits and visit reports.'}</p>
        </div>
        <span className="record-count"><ShieldCheck size={16} /> {responsibilities.data?.count ?? 0} records</span>
      </header>

      {isPlatformAdmin ? (
        <section className="content-card supervision-workspace-picker">
          <label>
            Operating organisation
            <select onChange={(event) => setPlatformWorkspace(event.target.value)} value={workspaceId}>
              <option value="">Select an agency or direct client</option>
              {availableOrganizations.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
          </label>
        </section>
      ) : null}

      {!workspaceId ? (
        <section className="content-card empty-state supervision-empty">
          <ShieldCheck size={28} />
          <h2>Select an operating organisation</h2>
          <p>Supervisor responsibilities are owned by the organisation operating each campaign.</p>
        </section>
      ) : (
        <>
          {canManage ? <section className="content-card management-panel supervision-transfer-panel">
            <div className="card-heading">
              <div>
                <h2>Assign or transfer stores</h2>
                <p>Every row takes effect together. Existing responsibility closes without rewriting its history.</p>
              </div>
              <ArrowRightLeft size={20} />
            </div>
            {options.isLoading ? (
              <div className="supervision-prerequisite" role="status">
                <InlineSpinner />
                <div><strong>Loading supervisors and stores</strong><p>Please wait while this workspace is prepared.</p></div>
              </div>
            ) : !hasSupervisors ? (
              <div className="supervision-prerequisite" role="status">
                <ShieldCheck size={22} />
                <div><strong>No active supervisors</strong><p>Add an active Supervisor membership before allocating campaign stores.</p></div>
              </div>
            ) : (
              <form className="management-form" onSubmit={submit}>
              <div className="form-grid supervision-transfer-meta">
                <label>
                  Effective at
                  <input min={minimumEffectiveAt} onChange={(event) => setEffectiveAt(event.target.value)} required type="datetime-local" value={effectiveAt} />
                </label>
                <label>
                  Handover reason
                  <input maxLength={240} onChange={(event) => setReason(event.target.value)} placeholder="Initial coverage or reason for transfer" required value={reason} />
                </label>
              </div>
              <div className="supervision-transfer-list">
                {transfers.map((transfer, index) => {
                  const selectedElsewhere = new Set(
                    transfers.filter((_, rowIndex) => rowIndex !== index).map((row) => row.location),
                  );
                  return (
                    <div className="supervision-transfer-row" key={index}>
                      <label>
                        Campaign store
                        <StorePicker
                          ariaLabel="Campaign store"
                          onChange={(location) => updateTransfer(index, { location })}
                          options={(options.data?.locations ?? []).filter((row) => !selectedElsewhere.has(row.id)).map((row) => ({
                            id: row.id,
                            name: row.venue_name,
                            address: row.campaign_name,
                            city: row.venue_city,
                          }))}
                          required
                          value={transfer.location}
                        />
                      </label>
                      <label>
                        Supervisor
                        <select required onChange={(event) => updateTransfer(index, { supervisor: event.target.value })} value={transfer.supervisor}>
                          <option value="">Select supervisor</option>
                          {options.data?.supervisors.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                        </select>
                      </label>
                      <button aria-label={`Remove transfer row ${index + 1}`} className="icon-button" disabled={transfers.length === 1} onClick={() => setTransfers((current) => current.filter((_, rowIndex) => rowIndex !== index))} type="button"><Trash2 size={17} /></button>
                    </div>
                  );
                })}
              </div>
              <div className="form-actions supervision-transfer-actions">
                <button className="button secondary" disabled={(options.data?.locations.length ?? 0) <= transfers.length} onClick={() => setTransfers((rowsValue) => [...rowsValue, { location: '', supervisor: '' }])} type="button"><Plus size={16} /> Add store</button>
                <button className="button primary" disabled={mutation.isPending || !options.data?.locations.length} type="submit">{mutation.isPending ? <InlineSpinner /> : <ArrowRightLeft size={16} />} Save responsibility change</button>
              </div>
              {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
              </form>
            )}
          </section> : null}

          <div className="section-heading"><div><h2>Responsibility history</h2><p>Current, scheduled and completed coverage remains visible to authorised workspace members.</p></div><span>{responsibilities.data?.count ?? 0}</span></div>
          <DataPanel loading={responsibilities.isLoading || options.isLoading} error={responsibilities.isError || options.isError} empty={rows.length === 0}>
            <section className="content-card data-card">
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Campaign and store</th><th>Supervisor</th><th>Effective period</th><th>Status</th></tr></thead>
                  <tbody>{rows.map((row) => {
                    const starts = new Date(row.effective_from).getTime();
                    const ends = row.effective_until ? new Date(row.effective_until).getTime() : null;
                    const state = starts > renderedAt ? 'scheduled' : ends && ends <= renderedAt ? 'completed' : 'active';
                    return <tr key={row.id}>
                      <td><strong>{row.venue_name}</strong><small>{row.campaign_name} · {row.venue_city}</small></td>
                      <td><strong>{row.supervisor_name}</strong><small>{row.reference}</small></td>
                      <td><strong>{dateTimeFormatter.format(new Date(row.effective_from))}</strong><small>{row.effective_until ? `Until ${dateTimeFormatter.format(new Date(row.effective_until))}` : 'No scheduled end'}</small></td>
                      <td><StatusPill value={state} /></td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            </section>
          </DataPanel>

          <div className="section-heading"><div><h2>Store visit activity</h2><p>Arrival, departure, duration and evidence validation for supervisor visits.</p></div><span>{visits.data?.count ?? 0}</span></div>
          <DataPanel loading={visits.isLoading} error={visits.isError} empty={visitRows.length === 0}>
            <section className="content-card data-card">
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Supervisor</th><th>Campaign and store</th><th>Arrival</th><th>Departure</th><th>Duration</th><th>Status</th></tr></thead>
                  <tbody>{visitRows.map((visit) => {
                    const flagged = visit.events.some((event) => event.validation_status === 'flagged');
                    const rejected = visit.events.some((event) => event.validation_status === 'rejected');
                    return <tr key={visit.id}>
                      <td><strong>{visit.supervisor_name}</strong><small>{visit.reference}</small></td>
                      <td><strong>{visit.venue_name}</strong><small>{visit.campaign_name} · {visit.venue_city}</small></td>
                      <td><strong>{visit.checked_in_at ? dateTimeFormatter.format(new Date(visit.checked_in_at)) : 'Rejected attempt'}</strong><small>{visit.events[0]?.distance_from_site_m ?? '—'} m from store</small></td>
                      <td><strong>{visit.checked_out_at ? dateTimeFormatter.format(new Date(visit.checked_out_at)) : '—'}</strong><small>{visit.status === 'open' ? 'Visit in progress' : 'Recorded evidence'}</small></td>
                      <td><strong>{formatDuration(visit.duration_seconds)}</strong><small>{visit.events.length} event{visit.events.length === 1 ? '' : 's'}</small></td>
                      <td><StatusPill value={rejected && visit.status !== 'completed' ? 'rejected' : flagged ? 'flagged' : visit.status} /></td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            </section>
          </DataPanel>

          <div className="section-heading"><div><h2>Visit reports</h2><p>Review complaints, coaching, product knowledge, and shelf or stock observations.</p></div><span>{reports.data?.count ?? 0}</span></div>
          <DataPanel loading={reports.isLoading} error={reports.isError} empty={reportRows.length === 0}>
            <section className="content-card data-card">
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Supervisor</th><th>Campaign and store</th><th>Activities</th><th>Complaints</th><th>Review</th><th>Report</th></tr></thead>
                  <tbody>{reportRows.map((report) => {
                    const activities = [report.training_provided && 'Training', report.product_knowledge_tested && 'Knowledge test', report.shelf_checked && 'Shelf check', report.stock_count_verified && 'Stock verification'].filter(Boolean);
                    return <tr key={report.id}>
                      <td><strong>{report.supervisor_name}</strong><small>{report.reference}</small></td>
                      <td><strong>{report.venue_name}</strong><small>{report.campaign_name} · {report.venue_city}</small></td>
                      <td><strong>{activities.length ? activities.join(', ') : 'Notes only'}</strong><small>{dateTimeFormatter.format(new Date(report.created_at))}</small></td>
                      <td><strong>{report.complaints.length}</strong><small>{report.complaints.some((item) => item.severity === 'high') ? 'Includes high severity' : 'Recorded items'}</small></td>
                      <td><StatusPill value={report.review_status} /></td>
                      <td><button className="button secondary" onClick={() => setSelectedReport(report)} type="button"><ClipboardCheck size={15} /> {canManage ? 'Review report' : 'View report'}</button></td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            </section>
          </DataPanel>
        </>
      )}
      {selectedReport ? <div className="confirmation-backdrop" role="presentation"><section aria-label="Supervisor visit report detail" aria-modal="true" className="confirmation-dialog shelf-evidence-dialog" role="dialog">
        <button aria-label="Close visit report" className="icon-button confirmation-close" onClick={closeReport}><X size={19} /></button>
        <p className="eyebrow">{selectedReport.reference}</p><h2>Supervisor visit report</h2>
        <div className="shelf-evidence-meta"><strong>{selectedReport.supervisor_name}</strong><span>{selectedReport.campaign_name} · {selectedReport.venue_name}</span><span>Submitted {dateTimeFormatter.format(new Date(selectedReport.created_at))} · Visit {selectedReport.visit_reference}</span></div>
        <section className="supervision-report-summary">
          <p><strong>Training:</strong> {selectedReport.training_provided ? selectedReport.training_topics || 'Provided' : 'Not recorded'}</p>
          <p><strong>Product knowledge:</strong> {selectedReport.product_knowledge_tested ? selectedReport.product_knowledge_result.replaceAll('_', ' ') : 'Not tested'}</p>
          {selectedReport.coaching_notes ? <p><strong>Coaching:</strong> {selectedReport.coaching_notes}</p> : null}
          <p><strong>Shelf checked:</strong> {selectedReport.shelf_checked ? 'Yes' : 'No'} · <strong>Stock verified:</strong> {selectedReport.stock_count_verified ? 'Yes' : 'No'}</p>
          {selectedReport.stock_observation ? <p><strong>Stock observation:</strong> {selectedReport.stock_observation}</p> : null}
          {selectedReport.general_notes ? <p><strong>Additional notes:</strong> {selectedReport.general_notes}</p> : null}
        </section>
        {selectedReport.complaints.length ? <section className="shelf-inventory-counts"><h3>Complaints</h3>{selectedReport.complaints.map((complaint) => <div key={complaint.id}><span><strong>{complaint.category.replaceAll('_', ' ')}</strong><small>{complaint.source.replaceAll('_', ' ')} · {complaint.severity} severity</small></span><span>{complaint.description}</span></div>)}</section> : <p className="shelf-inventory-empty">No complaints were recorded.</p>}
        {selectedReport.photos.length ? <section className="supervision-report-photos">{selectedReport.photos.map((photo) => <img alt={`Visit evidence at ${selectedReport.venue_name}`} key={photo.id} src={photo.photo} />)}</section> : null}
        {selectedReport.review_decisions.map((item) => <p key={item.id}><StatusPill value={item.decision} /> {item.reviewer_name}: {item.note || 'No note'}</p>)}
        {reportDecision ? <label>Review note {reportDecision === 'rejected' ? '(required)' : '(optional)'}<textarea onChange={(event) => setReportReviewNote(event.target.value)} rows={3} value={reportReviewNote} /></label> : null}
        {reportReviewMutation.isError ? <p className="form-error">{getApiErrorMessage(reportReviewMutation.error)}</p> : null}
        <div className="confirmation-actions">
          <button className="button secondary" onClick={closeReport}>Close</button>
          {selectedReport.can_decide && reportDecision !== 'rejected' ? <button className="button danger" onClick={() => selectReportDecision('rejected')}><XCircle size={15} /> Reject</button> : null}
          {selectedReport.can_decide && reportDecision !== 'approved' ? <button className="button primary" onClick={() => selectReportDecision('approved')}><CheckCircle2 size={15} /> Approve</button> : null}
          {reportDecision ? <button className="button primary" disabled={reportReviewMutation.isPending || (reportDecision === 'rejected' && !reportReviewNote.trim())} onClick={() => reportReviewMutation.mutate()}>{reportReviewMutation.isPending ? 'Saving…' : 'Confirm decision'}</button> : null}
        </div>
      </section></div> : null}
    </>
  );
}
